import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ButtonBase from '@material-ui/core/ButtonBase';
import InputBase from '@material-ui/core/InputBase';
import Autocomplete from '@material-ui/core/Autocomplete';
import {
  InputSearchIcon,
  Option,
  PopperWrapperStyled,
  SearchWrapper,
  useResidentListingStyles,
} from 'components/ResidentHeader/components/ResidentListing/styles';

type SelectProps = {
  options: { text: string; value: string }[];
  onSelect: (key: string) => void;
  initialValue?: string;
};

type AutocompleteCloseReason = 'toggleInput' | 'escape' | string;

function getText(value: string, options: { text: string; value: string }[]): string {
  const option = options.find((o) => o.value === value);
  return option?.text || '';
}

const DropdownWithSearch = ({ options, onSelect, initialValue }: SelectProps) => {
  const { t } = useTranslation();
  const classes = useResidentListingStyles();
  const [label, setLabel] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingValue, setPendingValue] = useState<{ text: string; value: string }[]>([]);
  const open = Boolean(anchorEl);
  const id = open ? 'popper-wrapper-id' : undefined;

  useEffect(() => {
    if (initialValue) {
      setLabel(getText(initialValue, options));
    }
  }, [initialValue, options]);

  const handleSelect = (key: string | null) => {
    if (key) {
      setLabel(getText(key, options));
      onSelect(key);
      setAnchorEl(null);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setPendingValue([]);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.ChangeEvent<unknown>, reason: AutocompleteCloseReason) => {
    if (reason === 'toggleInput') {
      return false;
    }
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
    return true;
  };

  return options.length ? (
    <>
      <div className={classes.root}>
        <ButtonBase
          disableRipple
          className={classes.button}
          aria-describedby={id}
          onClick={handleClick}
        >
          <h4>
            <span>{label}</span>
          </h4>
          <ArrowDropDownIcon fontSize="large" />
        </ButtonBase>
      </div>
      <PopperWrapperStyled
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        className={classes.popper}
      >
        {
          <Autocomplete
            open
            onClose={handleClose}
            multiple
            classes={{
              paper: classes.paper,
              option: classes.option,
              popperDisablePortal: classes.popperDisablePortal,
            }}
            value={pendingValue}
            onChange={(event, newValue: { text: string; value: string }[]) => {
              setPendingValue(newValue);
            }}
            disableCloseOnSelect
            disablePortal
            noOptionsText={t('noMatchFound')}
            renderOption={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: { text: string; value: string },
            ) =>
              option && option.value ? (
                <Option key={option.value} onClick={() => handleSelect(option.value)}>
                  <div>{option.text}</div>
                </Option>
              ) : null
            }
            options={[...options]}
            getOptionLabel={(option) => option.text}
            renderInput={(params) => (
              <SearchWrapper>
                <InputBase
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  className={classes.inputBase}
                />
                <InputSearchIcon />
              </SearchWrapper>
            )}
          />
        }
      </PopperWrapperStyled>
    </>
  ) : (
    <div />
  );
};

export default DropdownWithSearch;
